import { FormControl, FormHelperText, Stack } from "@mui/material";
import { CustomTextField } from "../ui/input";
import { useTranslations } from "next-intl";
import React from "react";
import { useTheme } from "@quales/utils/theme";
import { GQLIamUser } from "@quales/data-access/graphql";
import RoleList from "../composites/RoleList";
import { AppContext } from "@quales/utils/context";
import BranchAutoComplete from "../composites/Branches/composites/BranchAutoComplete";
import { TenantBranch } from "@quales/data-access/gql-server";
import { enqueueSnackbar } from "notistack";

interface UserFormProps {
  onUserDetails: (val: GQLIamUser) => void;
  data?: GQLIamUser;
}

interface UserFormError {
  emailAddress: string;
  lastName: string;
  role: string;
  firstName: string;
}

export const UserForm = React.forwardRef((props: UserFormProps, ref) => {
  const t = useTranslations();
  const { onUserDetails, data } = props;
  const theme = useTheme();
  const { localAppState } = React.useContext(AppContext);

  const extractedRoleData =
    data && data.roles && data.roles.length > 0
      ? data.roles.map((role) => ({
          name: role.roleName,
          //@ts-ignore
          id: role.roleID,
        }))
      : [];
  const [values, setValues] = React.useState<GQLIamUser>({
    emailAddress: "",
    firstName: "",
    infix: "",
    lastName: "",
    branchID: "",
    roles: [],
  });
  const [isCurrentUser, setIsCurrentUser] = React.useState(false);
  const [formIsSubmitted, setFormIsSubmitted] = React.useState(false);

  const [formErrors, setFormErrors] = React.useState<UserFormError>({
    emailAddress: t("feedback.missingValues"),
    firstName: t("feedback.missingValues"),
    lastName: t("feedback.missingValues"),
    role: t("feedback.missingValues"),
  });

  React.useEffect(() => {
    if (data) {
      setIsCurrentUser(data.userID === localAppState.userData.userID);

      setValues({
        emailAddress: data.emailAddress.trim() || "",
        firstName: data.firstName || "",
        infix: data.infix || "",
        lastName: data.lastName || "",
        //@ts-ignore
        roles: extractedRoleData || [],
        userID: data.userID,
        tenantID: data.tenantID,
        branchID: data.branchID,
      });
      checkRequiredValues(data);
      setFormIsSubmitted(true);
    }
  }, [data]);

  const handleChange =
    (prop: keyof GQLIamUser) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [prop]: event.target.value });
      if (Object.keys(formErrors).includes(prop)) {
        if (event.target.value !== "") {
          formErrors[prop] = "";
        } else {
          formErrors[prop] = t("feedback.missingValues");
        }
        setValues({ ...values, [prop]: event.target.value });
      } else {
        setValues({ ...values, [prop]: event.target.value });
      }
      setFormErrors({ ...formErrors });
    };

  const checkRequiredValues = (val: GQLIamUser) => {
    setFormErrors({
      emailAddress: val.emailAddress !== "" ? "" : t("feedback.missingValues"),
      lastName: val.lastName !== "" ? "" : t("feedback.missingValues"),
      firstName: val.firstName !== "" ? "" : t("feedback.missingValues"),
      role:
        val.roles && val.roles.length > 0 ? "" : t("feedback.missingValues"),
    });
  };

  const handleRoles = (roles: any[]) => {
    if (roles && roles.length > 0) {
      formErrors.role = "";
      setValues({ ...values, roles: roles });
    } else {
      formErrors.role = t("feedback.missingValues");
      setValues({ ...values, roles: [] });
    }

    setFormErrors({ ...formErrors });
  };

  React.useImperativeHandle(ref, () => ({
    onSubmit() {
      setFormIsSubmitted(true);
      if (Object.values(formErrors).some((item) => item.length > 0)) {
        enqueueSnackbar(t("feedback.missingValues"), {
          variant: "error",
        });
      } else {
        setFormErrors({
          emailAddress: "",
          firstName: "",
          lastName: "",
          role: "",
        });
        onUserDetails(values);
      }
    },
  }));

  return (
    <Stack
      id="QuotationForm"
      width="100%"
      direction="column"
      padding={8}
      alignItems={"center"}
    >
      <FormControl variant="outlined" sx={{ width: 250 }}>
        <CustomTextField
          controldisabled={isCurrentUser}
          id="outlined-adornment-email"
          label={t("labels.emailAddress")}
          value={values.emailAddress}
          required
          onChange={handleChange("emailAddress")}
          error={formIsSubmitted && formErrors.emailAddress.length > 0}
        />
        {formIsSubmitted && (
          <FormHelperText
            sx={{
              color: formErrors.emailAddress ? theme.appColors.error : "",
            }}
            id="filled-name-helper-text"
          >
            {formErrors.emailAddress ? formErrors.emailAddress : ""}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl variant="outlined" sx={{ width: 250 }}>
        <CustomTextField
          controldisabled={isCurrentUser}
          id="outlined-adornment-firstName"
          label={t("labels.firstName")}
          value={values.firstName}
          required
          onChange={handleChange("firstName")}
          error={formIsSubmitted && formErrors.firstName.length > 0}
        />
        {formIsSubmitted && (
          <FormHelperText
            sx={{
              color: formErrors.firstName ? theme.appColors.error : "",
            }}
            id="filled-name-helper-text"
          >
            {formErrors.firstName ? formErrors.firstName : ""}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl variant="outlined" sx={{ width: 250 }}>
        <CustomTextField
          controldisabled={isCurrentUser}
          id="outlined-adornment-infix"
          label={t("labels.infix")}
          value={values.infix}
          onChange={handleChange("infix")}
        />
      </FormControl>
      <FormControl variant="outlined" sx={{ width: 250 }}>
        <CustomTextField
          controldisabled={isCurrentUser}
          id="outlined-adornment-lastName"
          label={t("labels.lastName")}
          value={values.lastName}
          required
          onChange={handleChange("lastName")}
          error={formIsSubmitted && formErrors.lastName.length > 0}
        />
        {formIsSubmitted && (
          <FormHelperText
            sx={{
              color: formErrors.lastName ? theme.appColors.error : "",
            }}
            id="filled-name-helper-text"
          >
            {formErrors.lastName ? formErrors.lastName : ""}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ width: 250, marginLeft: 2 }}>
        <RoleList
          width={250}
          edit={!isCurrentUser}
          error={formIsSubmitted && formErrors.role.length > 0}
          InputPropsValue={extractedRoleData}
          onValueChanged={handleRoles}
        />

        {formIsSubmitted && (
          <FormHelperText
            sx={{
              color: formErrors.role ? theme.appColors.error : "",
            }}
            id="filled-name-helper-text"
          >
            {formErrors.role ? formErrors.role : ""}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ width: 250, marginLeft: 2 }}>
        {localAppState &&
          localAppState.enabledFeatures.length > 0 &&
          localAppState.enabledFeatures.includes("branches") && (
            <BranchAutoComplete
              width={250}
              //@ts-ignore
              defaultValue={values.branchID}
              onValueChanged={(val: number) => {
                setValues({
                  ...values,
                  branchID: val.toString(),
                });
              }}
            />
          )}
      </FormControl>
    </Stack>
  );
});

UserForm.displayName = "UserForm";

export default UserForm;
